import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { passwordManagerIgnoreProps } from '../../lib/password-manager';

export interface InlineEditableTextProps {
  /** Current value. The draft snapshots from this on every edit-mode entry. */
  value: string;
  /**
   * Called when the user commits a non-empty change (Enter or blur).
   * Receives the trimmed value. Not called when the value is unchanged or
   * empty after trim — the component reverts in those cases.
   */
  onCommit: (next: string) => void;
  /**
   * When true, renders the display children with no click affordance —
   * the text is read-only. Use to suppress editing without unmounting.
   */
  disabled?: boolean;
  /** Accessible label for the input and edit affordance (e.g. "Agent name"). */
  ariaLabel?: string;
  /**
   * Typography + layout classes for the shell, in both display and edit mode.
   * The `<input>` carries no font utilities of its own and inherits these, so
   * there is only ever one copy to keep in step with the `children`.
   *
   * Match whatever typography the `children` render. When the child is a
   * `PanelTitle`, pass `panelTitleTypography(size)` from
   * `@reflex/ui/components/layout/Typography` rather than hand-copying its
   * classes — the two are built from the same constants and cannot drift.
   *
   * Keep this to typography plus `min-w-0`. Layout classes like `block` or
   * `max-w-full` tailwind-merge over the shell's own `inline-flex` /
   * `max-w-[calc(100%+0.75rem)]` and truncate the value a padding-width early.
   */
  className?: string;
  /** Read-only display node. Click swaps it for an `<input>` carrying `value`. */
  children: ReactNode;
}

/**
 * Minimal click-to-edit text primitive. Renders `children` until clicked,
 * then swaps in an `<input>` seeded with `value`. Enter or blur commits;
 * Escape reverts.
 *
 * Wrap a styled display element (e.g. a `<PanelTitle as="span">`) as the
 * child — this component owns the editing affordance, the consumer owns
 * the visual treatment.
 */
export function InlineEditableText({
  value,
  onCommit,
  disabled,
  ariaLabel,
  className,
  children,
}: InlineEditableTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Snapshot the latest external `value` into the draft whenever we're
  // not editing — keeps the input seeded with fresh data when other
  // clients update the same field over WebSocket.
  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  function commit() {
    const next = draft.trim();
    if (next && next !== value) onCommit(next);
    setIsEditing(false);
  }

  function cancel() {
    setDraft(value);
    setIsEditing(false);
  }

  // Shrink-wrap the hover/focus chrome to the glyphs — never stretch across
  // the full header. `min-w-0` matters in a nowrap flex row: without it the
  // shell's automatic minimum clamps to the max width (100% of the row), so a
  // long value reserves the whole row and pushes trailing siblings (status
  // chips, badges) out of the container where they paint over adjacent content.
  //
  // `px-1.5` gives the hover/focus background breathing room and `-mx-1.5`
  // reclaims that inset so the text still lines up with the read-only version.
  // The max width is `calc(100% + 0.75rem)` — not `max-w-full` — precisely
  // because of that padding: capping the border box at 100% would leave the
  // content box 0.75rem (the `px-1.5`) narrower than the parent, truncating a
  // long value earlier when it becomes editable than when it is read-only (the
  // visible jump when a sidebar row is selected). Widening the cap by the
  // padding lets the border box absorb it while the negative margins keep the
  // margin box at 100%, so truncation lands at the same glyph in both states.
  const shellClass =
    'inline-flex w-max min-w-0 max-w-[calc(100%+0.75rem)] items-center rounded-md px-1.5 -mx-1.5';

  const textClass = cn(className);

  if (isEditing) {
    // Grid overlay: invisible mirror span sets the column width from the draft
    // text so the input tracks content without the browser's ~20ch default.
    //
    // Typography lives on the shell only. Tailwind's preflight sets
    // `font: inherit` and `letter-spacing: inherit` on form controls, so an
    // `<input>` carrying no font utilities renders at the shell's exact metrics
    // — as does the mirror, which has to measure in the same font for the
    // column width to be right. One copy of the caller's classes is the point:
    // the input cannot disagree with the read-only `children` about size,
    // weight, leading, or tracking, because there is nothing to disagree with.
    return (
      <span
        data-inline-editable="editing"
        className={cn(shellClass, 'inline-grid bg-primary/5 ring-1 ring-primary/40', textClass)}
      >
        <span
          aria-hidden
          // `min-w-0` keeps the mirror's minimum contribution from propping
          // the column open at the full draft width — in a tight flex row the
          // track shrinks with the shell instead of overflowing it.
          // `whitespace-pre` is explicit so an inherited `whitespace-nowrap`
          // (a `truncate` in `className`) cannot collapse the measurement.
          className="invisible col-start-1 row-start-1 min-w-0 whitespace-pre"
        >
          {draft || '\u00a0'}
        </span>
        <input
          ref={inputRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              cancel();
            }
          }}
          aria-label={ariaLabel}
          data-testid="inline-editable-input"
          className="col-start-1 row-start-1 w-full min-w-0 appearance-none border-0 bg-transparent p-0 outline-none"
          {...passwordManagerIgnoreProps}
        />
      </span>
    );
  }

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <span
      role="button"
      tabIndex={0}
      data-inline-editable="display"
      aria-label={ariaLabel ? `Edit ${ariaLabel}` : undefined}
      onClick={() => setIsEditing(true)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setIsEditing(true);
        }
      }}
      className={cn(
        shellClass,
        'cursor-text transition-colors hover:bg-primary/5 focus-visible:bg-primary/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40',
        textClass,
      )}
    >
      {children}
    </span>
  );
}
